import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Capability, JwtClaims } from '@hsc/types';
import { decodeJwt, isExpired } from './jwt.js';

export interface AuthContextValue {
  token: string | null;
  claims: JwtClaims | null;
  capabilities: Capability[];
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Storage adapter, default = localStorage when available. */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Storage key, default `hsc:jwt`. */
  storageKey?: string;
  /** Initial token override (tests). */
  initialToken?: string | null;
}

function defaultStorage(): AuthProviderProps['storage'] | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function AuthProvider({
  children,
  storage = defaultStorage(),
  storageKey = 'hsc:jwt',
  initialToken = null,
}: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(() => {
    if (initialToken !== null) return initialToken;
    return storage?.getItem(storageKey) ?? null;
  });

  const claims = useMemo<JwtClaims | null>(() => {
    if (!token) return null;
    const c = decodeJwt(token);
    if (!c || isExpired(c)) return null;
    return c;
  }, [token]);

  useEffect(() => {
    if (!storage) return;
    if (token) storage.setItem(storageKey, token);
    else storage.removeItem(storageKey);
  }, [token, storage, storageKey]);

  const login = useCallback((next: string) => setToken(next), []);
  const logout = useCallback(() => setToken(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      claims,
      capabilities: claims?.capabilities ?? [],
      isAuthenticated: claims !== null,
      login,
      logout,
    }),
    [token, claims, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
